import angular from "angular";

angular
    .module('api-sentinel')
    .factory('RelayService', RelayService);

RelayService.$inject = ['$resource', '$q', 'SENTINEL_API_HOST_CONSTANTS', 'SentinelUiSession'];

function RelayService($resource, $q, HOST, SentinelUiSession) {
    const api = $resource(HOST.URL + '/rest/1/admin/relay', {}, {
        getPermissions: {
            method: 'GET',
            url: HOST.URL + '/rest/1/admin/relay/permissions'
        },
        listOverrides: {
            method: 'GET',
            url: HOST.URL + '/rest/1/admin/relay/sentries',
            isArray: true
        },
        countOverrides: {
            method: 'GET',
            url: HOST.URL + '/rest/1/admin/relay/sentries/count'
        },
        searchSentries: {
            method: 'GET',
            url: HOST.URL + '/rest/1/admin/relay/sentries/search',
            isArray: true
        },
        setOverride: {
            method: 'PUT',
            params: { imei: '@imei', relayEnabled: '@relayEnabled' },
            url: HOST.URL + '/rest/1/admin/relay/sentries/:imei?relayEnabled=:relayEnabled'
        },
        clearOverride: {
            method: 'DELETE',
            params: { imei: '@imei' },
            url: HOST.URL + '/rest/1/admin/relay/sentries/:imei'
        },
        setOverridesBulk: {
            method: 'POST',
            url: HOST.URL + '/rest/1/admin/relay/sentries/bulk'
        },
        listHistory: {
            method: 'GET',
            url: HOST.URL + '/rest/1/admin/relay/history',
            isArray: true
        },
        countHistory: {
            method: 'GET',
            url: HOST.URL + '/rest/1/admin/relay/history/count'
        }
    });

    // Cached because the header asks on every render to decide whether to show the menu item.
    // Keyed by user, because logging out does not reload the page and the next person to sign in
    // on the same tab must not inherit the previous user's answer.
    let permissionsPromise = null;
    let permissionsUserId = null;

    const service = {
        permissions: permissions,
        searchSentries: searchSentries,
        listOverrides: listOverrides,
        countOverrides: countOverrides,
        setOverride: setOverride,
        clearOverride: clearOverride,
        setOverridesBulk: setOverridesBulk,
        listHistory: listHistory,
        countHistory: countHistory,
        resource: api
    };

    return service;

    function permissions() {
        const userId = SentinelUiSession.user ? SentinelUiSession.user.id : null;
        if (!permissionsPromise || permissionsUserId !== userId) {
            permissionsUserId = userId;
            const request = api.getPermissions().$promise.then(
                function (result) {
                    return { canEdit: !!result.canEdit, isEntitled: !!result.isEntitled };
                },
                function (error) {
                    // A caller with no access is refused outright. That is an answer, so it is
                    // cached as "no permissions" and keeps the menu quiet for everyone else.
                    if (error && error.status === 403) {
                        return { canEdit: false, isEntitled: false };
                    }
                    // Anything else is a failure to find out, so the next call asks again.
                    if (permissionsPromise === request) {
                        permissionsPromise = null;
                    }
                    return $q.reject(error);
                }
            );
            permissionsPromise = request;
        }
        return permissionsPromise;
    }

    // Only returns devices the caller could actually set, because the server applies the same
    // ownership rule the write does.
    function searchSentries(term, limit) {
        return api.searchSentries({ q: term, limit: limit || 10 });
    }

    function listOverrides(filter, search, page, itemsPerPage) {
        return api.listOverrides(queryFor(filter, search, page, itemsPerPage));
    }

    function countOverrides(filter, search, itemsPerPage) {
        return api.countOverrides(queryFor(filter, search, null, itemsPerPage));
    }

    function queryFor(filter, search, page, itemsPerPage) {
        const query = {};
        if (filter === 'enabled') {
            query.relayEnabled = true;
        } else if (filter === 'disabled') {
            query.relayEnabled = false;
        }
        if (search) {
            query.search = search;
        }
        if (page) {
            query.page = page;
        }
        if (itemsPerPage) {
            query.itemsPerPage = itemsPerPage;
        }
        return query;
    }

    function setOverride(imei, relayEnabled) {
        return api.setOverride({ imei: imei, relayEnabled: relayEnabled }, {});
    }

    function clearOverride(imei) {
        return api.clearOverride({ imei: imei });
    }

    function setOverridesBulk(imeis, relayEnabled) {
        return api.setOverridesBulk({}, { imeis: imeis, relayEnabled: relayEnabled });
    }

    function listHistory(filters, page, itemsPerPage) {
        return api.listHistory(historyQuery(filters, page, itemsPerPage));
    }

    function countHistory(filters, itemsPerPage) {
        return api.countHistory(historyQuery(filters, null, itemsPerPage));
    }

    function historyQuery(filters, page, itemsPerPage) {
        const query = {};
        const f = filters || {};
        if (f.imei) {
            query.imei = f.imei;
        }
        if (f.changedBy) {
            query.changedBy = f.changedBy;
        }
        if (f.from) {
            query.from = f.from;
        }
        if (f.to) {
            query.to = startOfNextDay(f.to);
        }
        if (page) {
            query.page = page;
        }
        if (itemsPerPage) {
            query.itemsPerPage = itemsPerPage;
        }
        return query;
    }

    // The date picker yields local midnight at the start of the chosen day, which as an upper bound
    // would exclude that whole day. The API treats 'to' as exclusive, so send the start of the next.
    function startOfNextDay(date) {
        if (!(date instanceof Date)) {
            return date;
        }
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    }
}
